import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

const CartContext = createContext(null);
const STORAGE_KEY = "dw_cart_items";

export function CartProvider({ children }) {
    const [items, setItems] = useState(() => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            return raw ? JSON.parse(raw) : [];
        } catch { return []; }
    });
    const [preview, setPreview] = useState({ items: [], subtotal: 0, shipping: 0, total: 0 });
    const [loading, setLoading] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);

    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    }, [items]);

    const refresh = useCallback(async () => {
        if (items.length === 0) {
            setPreview({ items: [], subtotal: 0, shipping: 0, total: 0 });
            return;
        }
        setLoading(true);
        try {
            const res = await api.post("/cart/preview", { items });
            setPreview(res.data);
        } catch (e) {
            console.error("cart preview failed", e);
        } finally {
            setLoading(false);
        }
    }, [items]);

    useEffect(() => { refresh(); }, [refresh]);

    const addItem = (product_id, variant_sku, qty = 1, opts = {}) => {
        setItems((prev) => {
            const existing = prev.find(i => i.product_id === product_id && i.variant_sku === variant_sku);
            if (existing) {
                return prev.map(i => i.product_id === product_id && i.variant_sku === variant_sku
                    ? { ...i, qty: i.qty + qty } : i);
            }
            return [...prev, { product_id, variant_sku, qty }];
        });
        if (!opts.silent) toast.success("Added to bag");
        if (opts.openDrawer !== false) setDrawerOpen(true);
    };
    const updateQty = (product_id, variant_sku, qty) => {
        if (qty <= 0) return removeItem(product_id, variant_sku);
        setItems(prev => prev.map(i =>
            i.product_id === product_id && i.variant_sku === variant_sku
                ? { ...i, qty } : i));
    };
    const removeItem = (product_id, variant_sku) => {
        setItems(prev => prev.filter(i => !(i.product_id === product_id && i.variant_sku === variant_sku)));
    };
    const clear = () => setItems([]);

    const count = items.reduce((s, i) => s + i.qty, 0);

    return (
        <CartContext.Provider value={{
            items, preview, loading, count,
            addItem, updateQty, removeItem, clear, refresh,
            drawerOpen, setDrawerOpen,
            openDrawer: () => setDrawerOpen(true),
            closeDrawer: () => setDrawerOpen(false),
        }}>
            {children}
        </CartContext.Provider>
    );
}

export const useCart = () => useContext(CartContext);
